<template>
  <section>
    <AlertBanner v-if="hasPhotoRejected" id="submit-error-alert" :status="0" class="mt-0 sm:mt-32 lg:mt-0" style="width: 100%">
      <div>
        Your profile photo did not meet our
        <a
          target="_blank"
          href="https://support.sittercity.com/hc/en-us/articles/115012280247-Profile-photo-guidelines-for-families"
          class="primary-link"
          >Photo Guidelines</a
        >. Check the guidelines and <a href="/account/seekers/photo#/upload" class="primary-link">upload a new photo</a>.
      </div>
    </AlertBanner>
    <DashboardLoadingSkeleton v-if="isLoading || outOfNetworkLoading" />
    <div v-else class="dashboard flex flex-col lg:flex-row lg:justify-center lg:gap-32">
      <GenericError v-if="hasError" />
      <section v-else class="dashboard-main px-16 lg:px-0">
        <DashboardHeading v-if="showHeader" v-once :account="account" :dashboard-data="dashboardData" />
        <OutOfNetworkCareModule :reservation-requests="reservationRequests" />
        <transition-group name="slide" tag="div">
          <GettingStarted
            v-show="showGettingStarted"
            key="fade-out"
            :account="account"
            :photo-url="dashboardData.photoUrl"
            class="w-full"
            @hide-getting-started="showGettingStarted = false"
          />
          <div v-if="hasActiveOrPendingJobs" key="slide-up">
            <h2 class="text-h1 mb-16">Jobs</h2>
            <JobsModule v-once :jobs="jobs" :eligible-jobs="eligibleJobs" :account="account" />
            <PremiumUpgradeModule v-if="basicAccount" v-once />
            <h1 v-if="showExplore" class="text-h1 mb-16">Explore Sittercity</h1>
            <NearbySittersList v-if="sitters.length > 0" :sitters="sitters" :zipcode="zipcode" :city="city" />
            <TrustSafetyModule :jobs="activeJobs" :account-type="account.accountType()" />
            <NearbyAdventuresList v-if="adventures.length > 0" :adventures="adventures" :zipcode="zipcode" :city="city" />
            <CategorizedPartnersList v-if="!basicAccount" :zip-code="zipcode" />
          </div>

          <!-- no active or pending jobs -->
          <div v-else key="slide-up-2">
            <h2 class="text-h1 mb-16">{{ requireJobPost || !hasJobHistory ? 'Start here: let’s create a job' : 'Jobs' }}</h2>

            <h3 v-if="!requireJobPost && hasJobHistory" class="text-h4 mb-4">Start your job post here:</h3>

            <p v-if="hasJobHistory" class="body-1 mb-16">
              Skip the scrolling and searching. Share how you need help and matching caregivers will reach out to you.
            </p>
            <p v-else class="body-1 mb-16">Share how you need help and caregivers will apply to introduce themselves.</p>

            <JobPostCarousel v-once :include-housekeeping="account.isCorporate()" />

            <PremiumUpgradeModule v-if="basicAccount" v-once />
            <h1 v-if="showExplore && !requireJobPost" class="text-h1 mb-16">Explore Sittercity</h1>
            <NearbySittersList v-if="sitters.length > 0" :sitters="sitters" :zipcode="zipcode" :city="city" />
            <TrustSafetyModule :jobs="activeJobs" :account-type="account.accountType()" />
            <NearbyAdventuresList
              v-if="adventures.length > 0 && !requireJobPost"
              :adventures="adventures"
              :zipcode="zipcode"
              :city="city"
            />
            <CategorizedPartnersList v-if="!requireJobPost && !basicAccount" :zip-code="zipcode" />
          </div>
        </transition-group>
      </section>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import AlertBanner from '@/components/alert_banner/AlertBanner.vue';
import CategorizedPartnersList from '@/components/bh_partners/CategorizedPartnersList.vue';
import GenericError from '@/components/generic_error/GenericError.vue';
import { refreshStalePhotoData } from '@/helpers/account/refreshStalePhotoData';
import { sendBrazeEvent } from '@/lib/braze';
import { PARENT_DASHBOARD_LOADED, SHOW_PARENT_SMS_MODAL } from '@/lib/constants/braze_custom_events';
import { setPhotoRejectCookie } from '@/lib/domain_cookie';
import { shouldShowSMSModal } from '@/lib/page';
import JobPresenter from '@/lib/presenters/job_presenter';
import { sendSegmentEvent } from '@/lib/segment';
import DashboardHeading from './DashboardHeading.vue';
import DashboardLoadingSkeleton from './DashboardLoadingSkeleton.vue';
import JobPostCarousel from './JobPostCarousel.vue';
import JobsModule from './JobsModule.vue';
import NearbyAdventuresList from './NearbyAdventuresList.vue';
import NearbySittersList from './NearbySittersList.vue';
import OutOfNetworkCareModule from './OutOfNetworkCareModule.vue';
import PremiumUpgradeModule from './PremiumUpgradeModule.vue';
import TrustSafetyModule from './TrustSafetyModule.vue';
import GettingStarted from './components/GettingStarted.vue';
import { useDashboardData, type DashboardJobEdge } from './useDashboardData';
import type { Account } from '@/models/account';

defineOptions({ name: 'Dashboard' });

const PHOTO_ALERT_DURATION_MS = 15000;
const PHOTO_ALERT_REMINDER_DAYS = 10;

const store = useStore();
const account = computed(() => store.getters['account/getAccount'] as Account);
const { dashboardData, sitters, adventures, reservationRequests, isLoading, hasError, outOfNetworkLoading } =
  useDashboardData(account);

const zipcode = account.value?.location?.zipcode ?? '';
const city = account.value?.location?.cityname ?? '';

const hasPhotoRejected = ref(false);
const showGettingStarted = ref(true);
let photoAlertTimeout: ReturnType<typeof setTimeout> | undefined;
let isUnmounted = false;

const jobs = computed<DashboardJobEdge[]>(() => dashboardData.value?.jobs.edges ?? []);
const hasJobHistory = computed(() => jobs.value.length !== 0);
const showHeader = computed(() => !account.value.isBasic() || hasJobHistory.value);
const requireJobPost = computed(() => {
  if (account.value.isCorporate()) return false;
  const jobCount = dashboardData.value?.jobs?.totalCount ?? 0;
  return jobCount === 0 && !account.value.hasPremiumHistory;
});
const eligibleJobs = computed(() => parseJobs(jobs.value));
const activeJobs = computed(() => jobs.value.filter((job) => job.node.isActive));
const pendingJobs = computed(() => jobs.value.filter((job) => job.node.status === 'PENDING'));
const hasActiveOrPendingJobs = computed(() => activeJobs.value.length > 0 || pendingJobs.value.length > 0);
const basicAccount = computed(() => !(account.value.isPremium() || account.value.isCorporate()));
const showExplore = computed(() => adventures.value.length > 0 || sitters.value.length > 0);

trackPostedJob();

onMounted(async () => {
  sendBrazeEvent(PARENT_DASHBOARD_LOADED);

  if (shouldShowSMSModal(account.value?.createdAt)) {
    sendBrazeEvent(SHOW_PARENT_SMS_MODAL);
  }

  try {
    await refreshStalePhotoData(store, account.value);
  } catch (error) {
    console.error('Failed to refresh account data on seeker dashboard:', error);
  }

  if (isUnmounted) return;

  hasPhotoRejected.value = setPhotoRejectCookie(
    account.value.photoStatus,
    `profilePhotoAlert-${account.value.id}`,
    'True',
    PHOTO_ALERT_REMINDER_DAYS
  );
  photoAlertTimeout = setTimeout(() => {
    hasPhotoRejected.value = false;
  }, PHOTO_ALERT_DURATION_MS);
});

onBeforeUnmount(() => {
  isUnmounted = true;
  clearTimeout(photoAlertTimeout);
});

function parseJobs(jobEdges: DashboardJobEdge[]) {
  const activeOrPending = jobEdges.map(({ node }) => node).filter((node) => node.isActive || node.status === 'PENDING');
  const inactiveEligible = activeOrPending.filter((node) => !node.isActive && new JobPresenter(node).isEligible());
  return activeOrPending.concat(inactiveEligible).slice(0, 2);
}

// The legacy job post flow redirects here with posted_job_family_type after a job is created.
function trackPostedJob() {
  const jobFamilyType = new URLSearchParams(window.location.search).get('posted_job_family_type');
  if (!jobFamilyType) return;

  sendSegmentEvent('Job Posted - Client', { family_type: jobFamilyType });
  window.history.replaceState(null, '', window.location.origin + window.location.pathname);
}
</script>

<style lang="scss" scoped>
.slide-move {
  transition: transform 1s;
}

.slide-enter-active {
  animation: slide-in 0.5s ease-out forwards;
  transition: opacity 0.5s;
}

.slide-leave-active {
  position: absolute;
  width: 95%;
  animation: slide-out 0.5s ease-out forwards;
  transition: opacity 0.5s;
  opacity: 0;
  @media only screen and (min-width: 1024px) {
    width: 668px;
  }
}

@keyframes slide-in {
  from {
    transform: translateY(-20px);
  }
  to {
    transform: translateY(0px);
  }
}
</style>
